import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Play, FileText } from "lucide-react";
import type { NoteName } from "../../lib/guitarEngine";
import type { ExplorerFilter, ExtendedChordQuality, InversionType } from "../../lib/chordEngine";
import {
  CHORD_CATEGORY_CHIPS,
  CHORD_STYLE_SHORTCUTS,
} from "./ChordSpeciesStrip";
import { getChordDef, getQualitiesByCategory } from "../../lib/chordEngine";

function DockDivider() {
  return <div className="w-px self-stretch bg-white/10 shrink-0 mx-0.5 min-h-[28px]" aria-hidden />;
}

function DockGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-1 shrink-0" title={hint}>
      <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500 px-0.5 leading-none">
        {label}
      </span>
      <div className="flex items-center gap-0.5">{children}</div>
    </div>
  );
}

function IconChip({
  active,
  disabled,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`h-8 min-w-[2rem] px-1.5 rounded-lg text-xs font-sans transition-all shrink-0 ${
        disabled ? "opacity-35 cursor-not-allowed" : ""
      } ${
        active
          ? "bg-[var(--bordona-accent)] text-white shadow-sm"
          : "bg-white/[0.07] text-slate-300 hover:bg-white/12"
      }`}
    >
      {children}
    </button>
  );
}

const VOICING_TYPES: { id: ExplorerFilter["voicingType"]; label: string; title: string }[] = [
  { id: "all", label: "Cerrado", title: "Notas juntas en el registro" },
  { id: "spread", label: "Abierto", title: "Notas repartidas en octavas" },
  { id: "drop2", label: "Drop 2", title: "Segunda nota aguda bajada una octava" },
  { id: "drop3", label: "Drop 3", title: "Tercera nota aguda bajada una octava" },
];

const INVERSION_LABELS: Record<InversionType, string> = {
  fundamental: "Fund.",
  first: "1ª inv.",
  second: "2ª inv.",
  third: "3ª inv.",
};

export interface ChordHorizontalDockProps {
  activeTab: "acorde" | "progresiones";
  onTabChange: (tab: "acorde" | "progresiones") => void;
  root: NoteName;
  qualityLabel: string;
  notes: readonly NoteName[];
  onPrevNote: () => void;
  onNextNote: () => void;
  onSelectNote: (n: NoteName) => void;
  onPrevQuality: () => void;
  onNextQuality: () => void;
  activeCategory: string;
  onCategoryChange: (id: string) => void;
  quality: ExtendedChordQuality;
  onQualityChange: (q: ExtendedChordQuality) => void;
  voicingType: ExplorerFilter["voicingType"];
  onVoicingType: (t: ExplorerFilter["voicingType"]) => void;
  inversion: InversionType;
  inversionOptions: InversionType[];
  onPrevInversion: () => void;
  onNextInversion: () => void;
  onPlayChord?: () => void;
  onCreateTask?: () => void;
  detectionScore?: number | null;
  isDetecting?: boolean;
}

export const ChordHorizontalDock: React.FC<ChordHorizontalDockProps> = (props) => {
  const {
    activeTab,
    onTabChange,
    root,
    qualityLabel,
    notes,
    onPrevNote,
    onNextNote,
    onSelectNote,
    onPrevQuality,
    onNextQuality,
    activeCategory,
    onCategoryChange,
    quality,
    onQualityChange,
    voicingType,
    onVoicingType,
    inversion,
    inversionOptions,
    onPrevInversion,
    onNextInversion,
    onPlayChord,
    onCreateTask,
    detectionScore,
    isDetecting,
  } = props;

  const [noteOpen, setNoteOpen] = useState(false);
  const invIndex = Math.max(0, inversionOptions.indexOf(inversion));
  const qualitiesInCat = getQualitiesByCategory(activeCategory);

  return (
    <div className="relative w-full flex flex-col gap-0">
      {noteOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default"
          aria-label="Cerrar selector"
          onClick={() => setNoteOpen(false)}
        />
      )}

      <div className="flex flex-wrap items-end gap-x-2 gap-y-1.5 px-2 py-2 min-h-[64px]">
        <DockGroup label="Módulo" hint="Acorde o progresión">
          <IconChip active={activeTab === "acorde"} onClick={() => onTabChange("acorde")} title="Un acorde">
            Acorde
          </IconChip>
          <IconChip active={activeTab === "progresiones"} onClick={() => onTabChange("progresiones")} title="Secuencia">
            Prog.
          </IconChip>
        </DockGroup>

        {activeTab === "acorde" && (
          <>
            <DockDivider />

            <DockGroup label="Raíz" hint="Tónica del acorde">
              <button
                type="button"
                onClick={onPrevNote}
                className="w-7 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white/10"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setNoteOpen((o) => !o)}
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--bordona-accent-hover)] to-[var(--bordona-accent)] text-white text-xl font-serif font-bold shadow-md"
                >
                  {root}
                </button>
                {noteOpen && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 p-2 rounded-xl bg-[#1a1a1a]/95 border border-white/15 shadow-2xl backdrop-blur-md">
                    <div className="grid grid-cols-6 gap-1 w-[min(16rem,70vw)]">
                      {notes.map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => {
                            onSelectNote(n);
                            setNoteOpen(false);
                          }}
                          className={`h-8 rounded-md text-xs font-bold ${
                            root === n
                              ? "bg-[var(--bordona-accent)] text-white"
                              : "bg-white/10 text-slate-200 hover:bg-white/15"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onNextNote}
                className="w-7 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white/10"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </DockGroup>

            <DockDivider />

            <DockGroup label="Calidad" hint="Tipo de acorde">
              <button type="button" onClick={onPrevQuality} className="w-7 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white/10">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="h-8 px-2 flex items-center text-sm font-serif italic text-slate-100 max-w-[5.5rem] truncate">
                {qualityLabel}
              </span>
              <button type="button" onClick={onNextQuality} className="w-7 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white/10">
                <ChevronRight className="w-4 h-4" />
              </button>
            </DockGroup>

            <DockDivider />

            <DockGroup label="Especie" hint="Familia del acorde">
              {CHORD_CATEGORY_CHIPS.map((cat) => (
                <IconChip
                  key={cat.id}
                  active={activeCategory === cat.id}
                  onClick={() => onCategoryChange(cat.id)}
                  title={cat.label}
                >
                  {cat.label}
                </IconChip>
              ))}
            </DockGroup>

            <DockDivider />

            <DockGroup label="Estilo" hint="Atajos de calidad">
              {CHORD_STYLE_SHORTCUTS.map((s) => (
                <IconChip
                  key={s.id}
                  active={quality === s.quality}
                  onClick={() => {
                    onCategoryChange(s.category);
                    onQualityChange(s.quality);
                  }}
                  title={s.label}
                >
                  {s.label}
                </IconChip>
              ))}
            </DockGroup>

            <DockDivider />

            <DockGroup label="Voicing" hint="Disposición en el teclado">
              {VOICING_TYPES.map((v) => (
                <IconChip
                  key={v.id}
                  active={voicingType === v.id}
                  onClick={() => onVoicingType(v.id)}
                  title={v.title}
                >
                  {v.label}
                </IconChip>
              ))}
            </DockGroup>

            <DockDivider />

            <DockGroup label="Inversión" hint="Qué nota queda abajo en el registro">
              <button
                type="button"
                onClick={onPrevInversion}
                disabled={inversionOptions.length <= 1}
                className="w-7 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white/10 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-[10px] font-sans font-semibold text-slate-200 px-1 tabular-nums">
                {INVERSION_LABELS[inversion]} ({invIndex + 1}/{inversionOptions.length})
              </span>
              <button
                type="button"
                onClick={onNextInversion}
                disabled={inversionOptions.length <= 1}
                className="w-7 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white/10 disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </DockGroup>

            <DockDivider />

            {onPlayChord && (
              <DockGroup label="Sonido">
                <button
                  type="button"
                  onClick={onPlayChord}
                  className="h-9 w-9 rounded-full bg-[var(--bordona-accent)] text-white flex items-center justify-center shadow-md hover:bg-[var(--bordona-accent-hover)]"
                >
                  <Play className="w-4 h-4 fill-current" />
                </button>
              </DockGroup>
            )}

            {onCreateTask && (
              <DockGroup label="Tarea">
                <button
                  type="button"
                  onClick={onCreateTask}
                  className="h-8 px-2.5 rounded-lg bg-white/[0.07] text-slate-300 hover:bg-white/12 flex items-center gap-1"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span className="text-[10px] font-sans">Tarea</span>
                </button>
              </DockGroup>
            )}

            {(isDetecting || detectionScore != null) && (
              <span className="text-[10px] font-sans text-slate-400 tabular-nums self-end pb-1">
                {isDetecting ? "● …" : detectionScore != null ? `${Math.round(detectionScore * 100)}%` : ""}
              </span>
            )}
          </>
        )}
      </div>

      {activeTab === "acorde" && (
        <div className="flex flex-wrap items-center gap-1 px-2 pb-2 pt-1 border-t border-white/8 max-h-[56px] overflow-y-auto scrollbar-thin">
          <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500 shrink-0 mr-1">
            Tipo
          </span>
          {qualitiesInCat.map((q) => {
            const qDef = getChordDef(q);
            return (
              <button
                key={q}
                type="button"
                onClick={() => onQualityChange(q)}
                className={`px-2 py-1 rounded-md text-[10px] font-sans transition-all shrink-0 ${
                  quality === q
                    ? "bg-[var(--bordona-accent)] text-white shadow-sm"
                    : "bg-white/[0.07] text-slate-300 hover:bg-white/12"
                }`}
                title={qDef?.degreeNames.join(" · ")}
              >
                {qDef?.label || q}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
